import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  History, Search, Users, Building2, Calendar, TrendingUp,
  Loader2, RefreshCw, ChevronLeft, ChevronRight, Clock, LogIn
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

export default function HistoricoAcessosPage() {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('30');
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0, has_more: false });

  useEffect(() => {
    loadHistory();
    loadStats();
  }, [pagination.skip]);

  useEffect(() => {
    loadStats();
  }, [filterPeriod]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-history?skip=${pagination.skip}&limit=${pagination.limit}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data.items);
        setPagination(prev => ({
          ...prev,
          total: data.total,
          has_more: data.has_more
        }));
      }
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-history/stats?days=${filterPeriod}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_more) {
      setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }));
    }
  };

  const filteredHistory = history.filter(h =>
    h.user_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.instituicao_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}`;
  };

  return (
    <div className="space-y-6" data-testid="historico-acessos-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <History className="w-6 h-6 text-[#0066CC]" />
            Histórico de Acessos
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Monitorize os acessos ao Portal das Instituições
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filterPeriod} onValueChange={setFilterPeriod}>
            <SelectTrigger className="w-[180px]" data-testid="period-filter">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { loadHistory(); loadStats(); }} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <LogIn className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {loadingStats ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : stats?.total_logins || 0}
            </p>
            <p className="text-xs text-slate-500">Total Logins</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold text-green-600">
              {loadingStats ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : stats?.unique_users || 0}
            </p>
            <p className="text-xs text-slate-500">Utilizadores Únicos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Building2 className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-purple-600">
              {loadingStats ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : stats?.unique_institutions || 0}
            </p>
            <p className="text-xs text-slate-500">Instituições Ativas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold text-amber-600">
              {loadingStats ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 
                stats?.total_logins && parseInt(filterPeriod) > 0
                  ? Math.round(stats.total_logins / parseInt(filterPeriod)) 
                  : 0
              }
            </p>
            <p className="text-xs text-slate-500">Média Diária ({filterPeriod}d)</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart and Top Users */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Daily Logins Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0066CC]" />
              Logins por Dia (Últimos 7 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : stats?.daily_logins?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.daily_logins}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDateShort} />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => `Data: ${value}`}
                    formatter={(value) => [`${value} logins`, 'Acessos']}
                  />
                  <Bar dataKey="count" fill="#0066CC" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-500">
                Sem dados para o período selecionado
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Users */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              Utilizadores Mais Ativos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : stats?.top_users?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_users.map((user, index) => (
                  <div key={user.user_id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm dark:text-white">{user.user_nome}</p>
                        <p className="text-xs text-slate-500">{user.instituicao_nome}</p>
                      </div>
                    </div>
                    <Badge variant="outline">{user.login_count} logins</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-500">
                Sem dados para o período selecionado
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por nome, email ou instituição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#0066CC]" />
              Registo de Acessos
            </span>
            <span className="text-sm font-normal text-slate-500">
              {pagination.total} registos
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utilizador</TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Data/Hora</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        Nenhum registo encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHistory.map((item) => (
                      <TableRow key={item.id} data-testid={`access-row-${item.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium dark:text-white">{item.user_nome}</p>
                            <p className="text-xs text-slate-500">{item.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="dark:text-white">{item.instituicao_nome}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={item.success ? 'bg-green-500' : 'bg-red-500'}>
                            {item.action === 'login' ? 'Login' : item.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {formatDate(item.timestamp)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.total > pagination.limit && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-slate-500">
                    A mostrar {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} de {pagination.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={pagination.skip === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={!pagination.has_more}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
